<script lang="ts">
  import { router } from "@inertiajs/svelte";
  import { untrack } from "svelte";
  import { authApi, usersApi } from "../lib/api";
  import { addNotification, uiStore, toggleTheme } from "../lib/stores";
  import Layout from "../components/layout/Layout.svelte";

  let currentTheme = $state("light");
  const unsubTheme = uiStore.subscribe((s) => (currentTheme = s.theme));

  let { user = null, profile = null }: {
    user?: { id: string; name: string; email: string; avatarUrl?: string; nickname?: string; locale?: string } | null;
    profile?: { id: string; name: string; email: string; nickname?: string; locale?: string; bio?: string; avatar_url?: string } | null;
  } = $props();

  let language = $state(untrack(() => profile?.locale || user?.locale || "ko"));
  // 통화 설정 (localStorage 기반)
  let currency = $state((typeof window !== "undefined" ? localStorage.getItem("app_currency") : null) || "KRW");
  // 알림 설정 (localStorage 기반)
  let scheduleAlert = $state((typeof window !== "undefined" ? localStorage.getItem("schedule_alert") : null) !== "false");
  let budgetAlert = $state((typeof window !== "undefined" ? localStorage.getItem("budget_alert") : null) !== "false");

  // 비밀번호 변경
  let currentPassword = $state("");
  let newPassword = $state("");
  let confirmPassword = $state("");
  let passwordLoading = $state(false);

  // 설정 저장 로딩
  let savingLocale = $state(false);

  // 계정 삭제 확인
  let showDeleteConfirm = $state(false);
  let deleteConfirmText = $state("");

  const LANGUAGES = [
    { code: "ko", label: "한국어" },
    { code: "en", label: "English" },
    { code: "ja", label: "日本語" },
    { code: "zh", label: "中文" },
  ];

  const CURRENCIES = [
    { code: "KRW", label: "원 (₩)" },
    { code: "USD", label: "달러 ($)" },
    { code: "JPY", label: "엔 (¥)" },
    { code: "EUR", label: "유로 (€)" },
    { code: "CNY", label: "위안 (¥)" },
    { code: "GBP", label: "파운드 (£)" },
    { code: "THB", label: "바트 (฿)" },
    { code: "SGD", label: "싱가포르 달러 (S$)" },
  ];

  async function saveLanguage() {
    savingLocale = true;
    try {
      const response = await fetch("/api/v1/users/me", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ locale: language }),
      });
      if (response.ok) {
        addNotification("success", "언어 설정이 저장되었습니다.");
      }
    } catch {
      addNotification("error", "저장에 실패했습니다.");
    } finally {
      savingLocale = false;
    }
  }

  function saveCurrency() {
    localStorage.setItem("app_currency", currency);
    addNotification("success", "기본 통화가 저장되었습니다.");
  }

  function saveNotifications() {
    localStorage.setItem("schedule_alert", String(scheduleAlert));
    localStorage.setItem("budget_alert", String(budgetAlert));
    addNotification("success", "알림 설정이 저장되었습니다.");
  }

  async function changePassword() {
    if (!currentPassword || !newPassword || !confirmPassword) {
      addNotification("error", "모든 항목을 입력해주세요.");
      return;
    }
    if (newPassword !== confirmPassword) {
      addNotification("error", "새 비밀번호가 일치하지 않습니다.");
      return;
    }
    if (newPassword.length < 8) {
      addNotification("error", "비밀번호는 8자 이상이어야 합니다.");
      return;
    }

    passwordLoading = true;
    try {
      const response = await fetch("/api/v1/users/me/password", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          current_password: currentPassword,
          password: newPassword,
          password_confirmation: confirmPassword,
        }),
      });
      if (response.ok) {
        currentPassword = "";
        newPassword = "";
        confirmPassword = "";
        addNotification("success", "비밀번호가 변경되었습니다.");
      } else {
        const data = await response.json();
        addNotification("error", data.errors?.[0]?.message || "비밀번호 변경에 실패했습니다.");
      }
    } catch {
      addNotification("error", "오류가 발생했습니다.");
    } finally {
      passwordLoading = false;
    }
  }

  async function handleLogout() {
    try {
      await authApi.logout();
      localStorage.removeItem("auth_token");
      addNotification("success", "로그아웃되었습니다.");
      router.visit("/");
    } catch {
      addNotification("error", "로그아웃에 실패했습니다.");
    }
  }

  async function deleteAccount() {
    if (deleteConfirmText !== "삭제") return;
    try {
      const response = await fetch("/api/v1/users/me", { method: "DELETE" });
      if (response.ok) {
        localStorage.removeItem("auth_token");
        addNotification("success", "계정이 삭제되었습니다.");
        router.visit("/");
      }
    } catch {
      addNotification("error", "계정 삭제에 실패했습니다.");
    }
  }
</script>

<Layout {user}>
  <div class="max-w-2xl mx-auto px-4 py-8 space-y-6">
    <!-- Header -->
    <div>
      <h1 class="text-2xl font-bold" style="color: var(--text-primary);">설정</h1>
      <p class="text-sm mt-1" style="color: var(--text-muted);">앱 환경을 설정하세요</p>
    </div>

    <!-- 계정 정보 -->
    <section class="rounded-2xl p-5 space-y-4" style="background: var(--surface); border: 1px solid var(--border);">
      <h2 class="text-base font-semibold" style="color: var(--text-primary);">계정</h2>
      <div class="flex items-center gap-4">
        <div class="w-14 h-14 rounded-full flex items-center justify-center text-xl font-bold flex-shrink-0"
          style="background: var(--accent-dim); color: var(--accent);">
          {#if profile?.avatar_url}
            <img src={profile.avatar_url} alt={profile.name} class="w-14 h-14 rounded-full object-cover" />
          {:else}
            {user?.name?.charAt(0) ?? "?"}
          {/if}
        </div>
        <div class="flex-1 min-w-0">
          <p class="font-semibold truncate" style="color: var(--text-primary);">{profile?.name || user?.name}</p>
          <p class="text-sm truncate" style="color: var(--text-muted);">{profile?.email || user?.email}</p>
          {#if profile?.nickname}
            <p class="text-xs mt-0.5" style="color: var(--text-muted);">@{profile.nickname}</p>
          {/if}
        </div>
        <a href="/profile/edit"
          class="px-4 py-2 rounded-xl text-sm font-medium transition-colors flex-shrink-0"
          style="background: var(--accent-dim); color: var(--accent);"
        >
          수정
        </a>
      </div>
    </section>

    <!-- 언어 설정 -->
    <section class="rounded-2xl p-5 space-y-4" style="background: var(--surface); border: 1px solid var(--border);">
      <h2 class="text-base font-semibold" style="color: var(--text-primary);">언어</h2>
      <div class="grid grid-cols-2 gap-2">
        {#each LANGUAGES as lang}
          <button
            onclick={() => (language = lang.code)}
            class="px-4 py-3 rounded-xl text-sm font-medium text-center transition-all"
            style="background: {language === lang.code ? 'var(--accent-dim)' : 'transparent'}; color: {language === lang.code ? 'var(--accent)' : 'var(--text-secondary)'}; border: 1px solid {language === lang.code ? 'rgba(32,178,170,0.3)' : 'var(--border)'};"
          >
            {lang.label}
          </button>
        {/each}
      </div>
      <button
        onclick={saveLanguage}
        disabled={savingLocale}
        class="w-full py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
        style="background: var(--accent); color: #000;"
      >
        {savingLocale ? "저장 중..." : "언어 저장"}
      </button>
    </section>

    <!-- 기본 통화 -->
    <section class="rounded-2xl p-5 space-y-4" style="background: var(--surface); border: 1px solid var(--border);">
      <h2 class="text-base font-semibold" style="color: var(--text-primary);">기본 통화</h2>
      <select
        bind:value={currency}
        class="w-full px-4 py-3 rounded-xl text-sm outline-none appearance-none cursor-pointer"
        style="background: var(--surface-hover); color: var(--text-primary); border: 1px solid var(--border);"
      >
        {#each CURRENCIES as c}
          <option value={c.code} style="background: var(--surface);">{c.label}</option>
        {/each}
      </select>
      <button
        onclick={saveCurrency}
        class="w-full py-2.5 rounded-xl text-sm font-medium transition-colors"
        style="background: var(--accent); color: #000;"
      >
        통화 저장
      </button>
    </section>

    <!-- 알림 설정 -->
    <section class="rounded-2xl p-5 space-y-4" style="background: var(--surface); border: 1px solid var(--border);">
      <h2 class="text-base font-semibold" style="color: var(--text-primary);">알림</h2>
      <div class="space-y-3">
        {#each [
          { label: "일정 알림", desc: "다가오는 일정을 미리 알려드립니다", bind: "scheduleAlert" },
          { label: "예산 초과 알림", desc: "예산이 초과될 때 알려드립니다", bind: "budgetAlert" },
        ] as item}
          <div class="flex items-center justify-between py-1">
            <div>
              <p class="text-sm font-medium" style="color: var(--text-primary);">{item.label}</p>
              <p class="text-xs mt-0.5" style="color: var(--text-muted);">{item.desc}</p>
            </div>
            <button
              aria-label={item.label}
              onclick={() => item.bind === "scheduleAlert" ? (scheduleAlert = !scheduleAlert) : (budgetAlert = !budgetAlert)}
              class="relative w-12 h-6 rounded-full transition-colors flex-shrink-0"
              style="background: {(item.bind === 'scheduleAlert' ? scheduleAlert : budgetAlert) ? 'var(--accent)' : 'var(--surface-hover)'};"
              role="switch"
              aria-checked={item.bind === "scheduleAlert" ? scheduleAlert : budgetAlert}
            >
              <span
                class="absolute top-0.5 w-5 h-5 rounded-full shadow transition-transform"
                style="background: white; transform: translateX({(item.bind === 'scheduleAlert' ? scheduleAlert : budgetAlert) ? '26px' : '2px'});"
              ></span>
            </button>
          </div>
        {/each}
      </div>
      <button
        onclick={saveNotifications}
        class="w-full py-2.5 rounded-xl text-sm font-medium transition-colors"
        style="background: var(--accent); color: #000;"
      >
        알림 설정 저장
      </button>
    </section>

    <!-- 테마 설정 -->
    <section class="rounded-2xl p-5 space-y-4" style="background: var(--surface); border: 1px solid var(--border);">
      <h2 class="text-base font-semibold" style="color: var(--text-primary);">테마</h2>
      <div class="grid grid-cols-2 gap-3">
        <button
          onclick={() => { if (currentTheme !== 'light') toggleTheme(); }}
          class="relative flex flex-col items-center gap-3 p-4 rounded-xl transition-all"
          style="background: {currentTheme === 'light' ? 'var(--accent-dim)' : 'transparent'}; border: 1px solid {currentTheme === 'light' ? 'rgba(32,178,170,0.3)' : 'var(--border)'};"
        >
          <!-- Light preview -->
          <div class="w-full aspect-[3/4] rounded-lg overflow-hidden" style="background: #F0EDE8; border: 1px solid rgba(0,0,0,0.08);">
            <div class="h-3 w-full" style="background: rgba(0,0,0,0.04);"></div>
            <div class="p-2 space-y-1.5">
              <div class="h-2 w-3/4 rounded-full" style="background: #1A1A1A; opacity: 0.7;"></div>
              <div class="h-1.5 w-1/2 rounded-full" style="background: #5A5A5A; opacity: 0.5;"></div>
              <div class="mt-2 h-6 w-full rounded" style="background: #FAFAF8; border: 1px solid rgba(0,0,0,0.06);"></div>
              <div class="h-6 w-full rounded" style="background: #FAFAF8; border: 1px solid rgba(0,0,0,0.06);"></div>
              <div class="h-6 w-full rounded" style="background: #FAFAF8; border: 1px solid rgba(0,0,0,0.06);"></div>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <svg class="w-4 h-4" style="color: {currentTheme === 'light' ? 'var(--accent)' : 'var(--text-muted)'};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <span class="text-sm font-medium" style="color: {currentTheme === 'light' ? 'var(--accent)' : 'var(--text-secondary)'};">라이트</span>
          </div>
          {#if currentTheme === 'light'}
            <div class="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style="background: var(--accent);">
              <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          {/if}
        </button>

        <button
          onclick={() => { if (currentTheme !== 'dark') toggleTheme(); }}
          class="relative flex flex-col items-center gap-3 p-4 rounded-xl transition-all"
          style="background: {currentTheme === 'dark' ? 'var(--accent-dim)' : 'transparent'}; border: 1px solid {currentTheme === 'dark' ? 'rgba(32,178,170,0.3)' : 'var(--border)'};"
        >
          <!-- Dark preview -->
          <div class="w-full aspect-[3/4] rounded-lg overflow-hidden" style="background: #080808; border: 1px solid rgba(255,255,255,0.07);">
            <div class="h-3 w-full" style="background: rgba(255,255,255,0.04);"></div>
            <div class="p-2 space-y-1.5">
              <div class="h-2 w-3/4 rounded-full" style="background: rgba(255,255,255,0.8);"></div>
              <div class="h-1.5 w-1/2 rounded-full" style="background: rgba(255,255,255,0.4);"></div>
              <div class="mt-2 h-6 w-full rounded" style="background: #1A1A1A; border: 1px solid rgba(255,255,255,0.07);"></div>
              <div class="h-6 w-full rounded" style="background: #1A1A1A; border: 1px solid rgba(255,255,255,0.07);"></div>
              <div class="h-6 w-full rounded" style="background: #1A1A1A; border: 1px solid rgba(255,255,255,0.07);"></div>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <svg class="w-4 h-4" style="color: {currentTheme === 'dark' ? 'var(--accent)' : 'var(--text-muted)'};" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
            <span class="text-sm font-medium" style="color: {currentTheme === 'dark' ? 'var(--accent)' : 'var(--text-secondary)'};">다크</span>
          </div>
          {#if currentTheme === 'dark'}
            <div class="absolute top-2 right-2 w-5 h-5 rounded-full flex items-center justify-center" style="background: var(--accent);">
              <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          {/if}
        </button>
      </div>
      <p class="text-xs text-center" style="color: var(--text-muted);">시스템 설정에 따라 자동으로 전환됩니다</p>
    </section>

    <!-- 비밀번호 변경 -->
    <section class="rounded-2xl p-5 space-y-4" style="background: var(--surface); border: 1px solid var(--border);">
      <h2 class="text-base font-semibold" style="color: var(--text-primary);">비밀번호 변경</h2>
      <div class="space-y-3">
        <div>
          <label for="setting-current-pw" class="block text-xs font-medium mb-1.5" style="color: var(--text-muted);">현재 비밀번호</label>
          <input
            id="setting-current-pw"
            type="password"
            placeholder="현재 비밀번호 입력"
            bind:value={currentPassword}
            class="w-full px-4 py-3 rounded-xl text-sm outline-none"
            style="background: var(--surface-hover); color: var(--text-primary); border: 1px solid var(--border);"
          />
        </div>
        <div>
          <label for="setting-new-pw" class="block text-xs font-medium mb-1.5" style="color: var(--text-muted);">새 비밀번호</label>
          <input
            id="setting-new-pw"
            type="password"
            placeholder="8자 이상"
            bind:value={newPassword}
            class="w-full px-4 py-3 rounded-xl text-sm outline-none"
            style="background: var(--surface-hover); color: var(--text-primary); border: 1px solid var(--border);"
          />
        </div>
        <div>
          <label for="setting-confirm-pw" class="block text-xs font-medium mb-1.5" style="color: var(--text-muted);">새 비밀번호 확인</label>
          <input
            id="setting-confirm-pw"
            type="password"
            placeholder="새 비밀번호 다시 입력"
            bind:value={confirmPassword}
            class="w-full px-4 py-3 rounded-xl text-sm outline-none"
            style="background: var(--surface-hover); color: var(--text-primary); border: 1px solid var(--border);"
          />
        </div>
      </div>
      <button
        onclick={changePassword}
        disabled={passwordLoading}
        class="w-full py-2.5 rounded-xl text-sm font-medium transition-colors disabled:opacity-50"
        style="background: var(--surface-hover); color: var(--text-primary);"
      >
        {passwordLoading ? "변경 중..." : "비밀번호 변경"}
      </button>
    </section>

    <!-- 앱 정보 -->
    <section class="rounded-2xl p-5" style="background: var(--surface); border: 1px solid var(--border);">
      <h2 class="text-base font-semibold mb-3" style="color: var(--text-primary);">앱 정보</h2>
      <div class="space-y-2 text-sm" style="color: var(--text-muted);">
        <div class="flex justify-between">
          <span>버전</span>
          <span>1.0.0</span>
        </div>
        <div class="flex justify-between">
          <span>디자인</span>
          <span>Dual Theme (Light/Dark)</span>
        </div>
      </div>
    </section>

    <!-- 로그아웃 -->
    <section class="rounded-2xl p-5 space-y-3" style="background: var(--surface); border: 1px solid var(--border);">
      <h2 class="text-base font-semibold" style="color: var(--text-primary);">계정 관리</h2>
      <button
        onclick={handleLogout}
        class="w-full py-3 rounded-xl text-sm font-medium transition-colors text-left px-4"
        style="background: rgba(248,113,113,0.08); color: #f87171; border: 1px solid rgba(248,113,113,0.15);"
      >
        <span class="flex items-center gap-2">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          로그아웃
        </span>
      </button>

      {#if !showDeleteConfirm}
        <button
          onclick={() => (showDeleteConfirm = true)}
          class="w-full py-3 rounded-xl text-sm font-medium text-left px-4 transition-colors"
          style="background: transparent; color: rgba(248,113,113,0.5); border: 1px solid rgba(248,113,113,0.1);"
        >
          계정 삭제
        </button>
      {:else}
        <div class="space-y-3 p-4 rounded-xl" style="background: rgba(248,113,113,0.06); border: 1px solid rgba(248,113,113,0.2);">
          <p class="text-sm font-medium" style="color: #f87171;">⚠️ 계정을 삭제하면 모든 데이터가 영구 삭제됩니다.</p>
          <p class="text-xs" style="color: var(--text-muted);">확인하려면 아래에 <strong style="color: #f87171;">삭제</strong>라고 입력하세요.</p>
          <input
            type="text"
            placeholder="삭제"
            bind:value={deleteConfirmText}
            class="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style="background: rgba(255,255,255,0.05); color: var(--text-primary); border: 1px solid rgba(248,113,113,0.3);"
          />
          <div class="flex gap-2">
            <button
              onclick={() => { showDeleteConfirm = false; deleteConfirmText = ""; }}
              class="flex-1 py-2 rounded-lg text-sm font-medium"
              style="background: var(--surface-hover); color: var(--text-secondary);"
            >
              취소
            </button>
            <button
              onclick={deleteAccount}
              disabled={deleteConfirmText !== "삭제"}
              class="flex-1 py-2 rounded-lg text-sm font-medium disabled:opacity-30 transition-opacity"
              style="background: rgba(248,113,113,0.2); color: #f87171;"
            >
              영구 삭제
            </button>
          </div>
        </div>
      {/if}
    </section>
  </div>
</Layout>
